// CsvUploader.js
import React, { useState, useCallback, useEffect } from 'react';
import { Button, Typography, Box, IconButton, Chip, CircularProgress } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { useDropzone } from 'react-dropzone';
import { api } from '../../services/api';
import { isPending } from '../../services/apiUtils.js';


const CsvUploader = ({
  setUserData,
  userData,
  onTopicUpdate,
  setError,
  setWarningMessage, 
  setOpenWarningSnackbar,
  maxPersonas,
  isAdmin,
  hasActiveSubscription,
  setCurrentPage,
  setSelectedAttribute,
}) => {
  /* ───────────────────────── State ───────────────────────── */
  const [uploadedFileName, setUploadedFileName] = useState(userData.importedPersonasFile);
  const [uploadedPersonas, setUploadedPersonas] = useState(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  const hasResponses = userData?.surveyResponses && userData.surveyResponses.length > 0;


  useEffect(() => {
    console.log("in CSVUPLOADER, userData is: ", userData);
    if (userData.importedPersonas) {
        setSaved(true);
        setLoading(false);
      
    } else {
        setSaved(false);
        setLoading(false);      
    }
  }, [userData.importedPersonas]);

  useEffect(() => {
    if (userData.importedPersonasFile) {
      setUploadedFileName(userData.importedPersonasFile);
    } else {
      setUploadedFileName(null);
    }
  }, [userData.importedPersonasFile]);



  /* ─────────────────── Handle incoming CSV file ─────────────────── */
  const handleFile = useCallback(
    async (file) => {
      if (!file) return;

      if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
        setError('Only CSV files are allowed.');
        return;
      }

      try {
        // setLoading(true);
        setError(null);

        const fileText = await file.text();
        const results = parseCSV(fileText);


        if (CsvIsEmpty(results)) {
          setError('The CSV file appears to be empty. Please check the file and try again.');
          return;
        }

        const headers = results.data[0];
        const personas = [];

        for (let i = 1; i < results.data.length; i++) {
          const row = results.data[i];
          if (row.length < headers.length) continue; // Skip incomplete rows
        
          const persona = {};
        
          headers.forEach((header, idx) => {
            persona[header.trim()] = row[idx] ? row[idx].trim() : '';
          });
        
          personas.push(persona);
        }
        
        if (!personas.length) {
          setError('Could not extract valid personas from the CSV. Please check the format.');
          return;
        }

        
        setUploadedFileName(file.name);
        setUploadedPersonas(personas);
        setSaved(false);

        
      } catch (err) {
        console.error('Error processing CSV:', err);
        setError("Failed to process the CSV file. Please ensure it's a valid CSV format.");
      } finally {
        // setLoading(false);
      }
    },
    [setUserData, userData, onTopicUpdate, setError, setLoading]
  );

  const handleSave = async () => {
    if (Object.keys(uploadedPersonas).length > maxPersonas) {
      let errorMessage = '';
      if (isAdmin) {
        errorMessage = `Admin accounts are limited to ${maxPersonas} personas per research.`;
      } else if (hasActiveSubscription) {
        errorMessage = `Your subscription plan is limited to ${maxPersonas} personas per research.`;
      } else {
        errorMessage = `Free research is limited to ${maxPersonas} personas. Please reduce the number of personas or upgrade to a subscription plan.`;
      }
      
      setError(errorMessage);
      return;
    }

    if (isPending(userData, setWarningMessage, setOpenWarningSnackbar)) {
        setLoading(false);
        return;
      }
  
      if (hasResponses) {
        const proceed = window.confirm(
          'After re-generating personas, you will have to re-generate responses. Do you want to proceed?'
        );
        if (!proceed) {
          return;
        }
      } 


    setLoading(true);

    let personas;
    try {
        personas = await api.importPersonas({
            userID: userData.userID,
            researchID: userData.researchID,
            personas: uploadedPersonas,
            fileName: uploadedFileName,
            });
    } catch (err) {
        console.log("Error when importing personas", err);
        setError("Error on the server, please try again later or contact us if this issue keeps reoccurring");
        setLoading(false);
        return;
    }

    const newUserData = {
        ...userData,
        selectedPersonas: personas,
        personasStatus: 'COMPLETED',
        lastModified: Date.now() / 1000,
        responsesStatus: 'NOT_STARTED',
        surveyResponses: null,
        importedPersonas: true,
        importedPersonasFile: uploadedFileName,
    };

    setUserData(newUserData);
    if (onTopicUpdate) onTopicUpdate(newUserData);

    setSaved(true);
    setLoading(false);

    setCurrentPage(1);

    // Simply select the first attribute TODO: this can be done better in the future, making it consistent with AIPersonaGenerator
    const firstNonNameProperty = Object.keys(Object.values(personas)[0])
    .filter(key => key !== "name")[0];

    setSelectedAttribute(firstNonNameProperty);
  };



const parseCSV = (text, opts = {}) => {
  const delimiters     = opts.delimiters || [',', ';', '\t', '|'];
  const forceDelimiter = opts.forceDelimiter;

  // 1. Split into physical lines (keep non-empty ones)
  const lines = text
    .split(/\r\n|\n|\r/)
    .filter((l) => l.trim().length);

  if (!lines.length) return { delimiter: ',', data: [] };

  // 2. Pick delimiter
  const delimiter = forceDelimiter || detectDelimiter(lines[0], delimiters);

  // 3. Parse every line
  const data = lines.map((line) => parseLine(line, delimiter));

  return { delimiter, data };
};

/* ---------- helpers ------------------------------------------------------ */

function detectDelimiter(line, delimiters) {
  // character-by-character scan, ignoring delimiters that appear inside quotes
  const counts = Object.fromEntries(delimiters.map((d) => [d, 0]));
  let inQuote  = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (ch === '"' && (i === 0 || line[i - 1] !== '\\')) {
      inQuote = !inQuote;
    } else if (!inQuote && counts.hasOwnProperty(ch)) {
      counts[ch]++;
    }
  }

  // choose the delimiter with the highest count; default to comma
  let best = ',';
  let max  = -1;
  for (const d of delimiters) {
    if (counts[d] > max) {
      max  = counts[d];
      best = d;
    }
  }
  return best;
}

function parseLine(line, delimiter) {
  const row     = [];
  let   inQuote = false;
  let   cur     = '';

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (ch === '"' && (i === 0 || line[i - 1] !== '\\')) {
      inQuote = !inQuote;
    } else if (ch === delimiter && !inQuote) {
      row.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  row.push(cur.trim());

  // strip surrounding quotes if present
  return row.map((cell) => cell.replace(/^"(.*)"$/, '$1').trim());
}

function CsvIsEmpty(results) {
  // Check if results.data doesn't exist
  if (!results.data) {
    return true;
  }
  
  // Check if results.data has no items (is empty)
  if (Array.isArray(results.data) && results.data.length === 0) {
    return true;
  }
  
  // Check if results.data[0] has no items
  if (!results.data[0] || (Array.isArray(results.data[0]) && results.data[0].length === 0)) {
    return true;
  }
  
  // Check if results.data[0] has only items that are either empty string, null, or undefined
  if (Array.isArray(results.data[0])) {
    // Every item is either empty string, null, or undefined
    return results.data[0].every(item => item === "" || item === null || item === undefined);
  }
  
  // If none of the conditions are met, return false
  return false;
}




  /* ───────────────────── Remove / reset file ───────────────────── */
  const handleRemoveFile = () => {
    setUploadedFileName(null);
    setSaved(false);
    setUserData((prevUserData) => ({
      ...prevUserData,
      importedPersonasFile: null, // or an empty string, depending on your logic
    }));
  };

  /* ───────────────────── Dropzone setup ───────────────────── */
  const onDrop = useCallback(
    (accepted) => accepted[0] && handleFile(accepted[0]),
    [handleFile]
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    noClick: true, // we control clicks via our button
    accept: { 'text/csv': ['.csv'] },
    multiple: false,
  });

  /* ───────────── Sample CSV template download helper ───────────── */
  const handleDownloadSampleCSV = () => {
    const headers = [
      'name',
      'age_range',
      'gender',
      'occupation',
      'income_level',
      'education',
      'interests',
      'location',
    ];
    
    const sampleRows = [
      ['John Doe', '35-44', 'Male', 'Software Engineer', 'High', 'Bachelor', 'Hiking, Reading', 'New York'],
      ['Jane Smith', '25-34', 'Female', 'Marketing Manager', 'Medium', 'Master', 'Travel, Cooking', 'San Francisco'],
      ['Alex Johnson', '45-54', 'Non-binary', 'Teacher', 'Medium', 'PhD', 'Music, Gardening', 'Chicago'],
    ];
    
    // Function to properly quote and escape CSV values
    const escapeCSV = (value) => {
      // If the value contains commas, quotes, or newlines, wrap it in quotes
      if (/[",\n\r]/.test(value)) {
        // Double up any quotes within the value
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };
    
    // Map each row and escape each value
    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...sampleRows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');
    
    const url = URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample_personas.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  

  /* ─────────────────────────── Render ─────────────────────────── */
  return (
    <div>
      {/* Information text and sample template download button */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        CSV must include column headers for your persona attributes
      </Typography>

      <Button variant="text" size="small" onClick={handleDownloadSampleCSV} sx={{ mb: 2 }}>
        Download Sample Template
      </Button>

      {/* ========= 1️⃣  BEFORE upload – drop-area ========= */}
      {!uploadedFileName && (
        <Box
          {...getRootProps()}
          sx={{
            position: 'relative',
            width: '100%',
            height: 220,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.400',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            mb: 1,
            p: 2,
          }}
        >
          <input {...getInputProps()} />

          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={open}
          >
            Choose File
          </Button>

          {isDragActive && (
            <Typography variant="body2" mt={1}>
              Drop the CSV here…
            </Typography>
          )}

          {!isDragActive && (
            <Typography
              variant="body2"
              color="text.secondary"
              mt={1}
              align="center"
            >
              Drag & drop your CSV here
              <br />
              or click "Choose File"
            </Typography>
          )}
        </Box>
      )}

      {/* ========= 2️⃣  AFTER upload – file badge ========= */}
      {uploadedFileName && (
      <Box
          sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.400',
          borderRadius: 1.5,
          px: 2,
          py: 1,
          mb: 2,
          maxWidth: 420,
          }}
      >
          <CloudUploadIcon 
            sx={{ 
              color: saved ? 'success.main' : 'grey.500', 
              fontSize: 20, 
              mr: 1.5 
            }} 
          />

          <Typography
          variant="body2"
          sx={{ flexGrow: 1, wordBreak: 'break-all', pr: 1 }}
          >
          {uploadedFileName} {!saved && <span style={{ color: '#757575', fontSize: '0.8em' }}></span>}
          </Typography>

          <IconButton size="small" onClick={handleRemoveFile}>
          <CloseIcon fontSize="small" />
          </IconButton>
      </Box>
      )}

      <Button
        variant="contained"
        onClick={handleSave}
        type="submit"
        disabled={saved || !uploadedFileName}
        sx={{ mt: 2, mr: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : (saved ? 'Uploaded' : 'Upload Personas')}
      </Button>
    </div>
  );
};

export default CsvUploader;
